import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import { api, ErroreApi } from '../servizi/api.ts'
import { useAree, useCatalogo, usePaesi, useSessione } from '../servizi/sessione.tsx'
import {
  AreaTesto, Bottone, Campo, Etichetta, EtichettaArea, Modale, Riquadro, Scelta, Spunta, Tabella, totaleDi,
  type ConTotale, type Opzione, type TonoEtichetta,
} from '../componenti/base.tsx'
import { Attesa, Avviso, Caselle, Dato, Intestazione, Vuoto } from '../componenti/Pezzi.tsx'
import { IconaIndietro, IconaPiu } from '../componenti/Icone.tsx'
import { AIUTO_TELEFONO, emailAccettabile, telefonoAccettabile, telefonoInternazionale } from '../servizi/contatti.ts'
import { aiutoRicerca, comeArrivano, useTrovati } from './Registro.tsx'
import { useFuocoSulPrimoErrore } from '../componenti/persona-campi.tsx'
import { AvvisoModulo, erroriDellApi, type Etichette } from '../componenti/errori-modulo.tsx'
import { vociSedi } from '../servizi/sedi.ts'
import { UnisciConEnte } from './enti/UnisciEnti.tsx'

/**
 * Scheda di un ente (D-23): anagrafica, sede legale e contatti, sedi CIDIS collegate,
 * rappresentante legale, referenti, progetti collegati e servizi erogati, con il modulo di
 * inserimento e modifica. I permessi letti qui servono solo a non mostrare comandi inutili: il
 * perimetro lo decide l'API, che fuori perimetro risponde 404.
 */

const ITALIA = 'ITA'

export type ComuneScelto = {
  codice_istat: string; nome: string; provincia_sigla: string | null; soppresso_il?: string | null
}
type SedeCollegata = { id: string; codice: string; nome: string; attiva: boolean }
type Referente = {
  id: string; nome: string; cognome: string; ruolo: string | null; ufficio: string | null
  email: string | null; telefono: string | null; rappresentante_legale: boolean; attivo: boolean
}
type ProgettoCollegato = {
  id: string; codice_cidis: string | null; titolo: string; data_inizio: string | null; data_fine: string | null
  ruoli: string[]; stato_date: 'da_avviare' | 'in_corso' | 'concluso' | null
}
export type DettaglioEnte = {
  id: string; sigla: string | null; nome: string
  categoria_ente: string | null; categoria_nome: string | null
  profilo_ente: string | null; profilo_nome: string | null; profilo: string | null
  categoria_storica: string; natura: string | null; natura_nome: string | null; cf_piva: string | null
  paese: string | null; paese_nome: string | null; comune: string | null; comune_nome: string | null
  provincia_sigla: string | null; cap: string | null; citta: string | null; indirizzo: string | null
  email: string | null; telefono: string | null; attivo: boolean; legacy_id: number | null
  /** «Finanziatore?» e «Partner formale?» (478). */
  finanziatore: string | null; partner_formale: string | null
  sostituito_da_sede: string | null; sostituito_da_sede_nome: string | null
  sostituito_da_sede_codice: string | null
  creato_il: string; aggiornato_il: string | null
  sedi: SedeCollegata[]; rappresentante_legale: Referente | null; referenti: Referente[]
  progetti: ProgettoCollegato[]; articolazioni: Articolazione[]
  /** Enti uniti a questo come doppioni (475): nome e acronimo restano come alias. */
  altri_nomi: { id: string; nome: string; sigla: string | null; legacy_id: number | null; unito_il: string | null }[]
}
/** Dipartimento, filiale, ufficio o sede dell'ente (Osservazioni 3, punto 9). */
export type Articolazione = {
  id: string; nome: string; nota: string | null; attiva: boolean; servizi: number
}
type RigaServizio = ConTotale & {
  id: string; data: string; tipologia: string; area_codice: string; sede: string | null
  ore: number | null; da_verificare: boolean; annullato: boolean
}

// --- aiuti -------------------------------------------------------------------------------------

/** Luogo leggibile: comune e provincia per l'Italia, citta' e paese per l'estero. */
export function luogoEnte(e: {
  paese: string | null; paese_nome?: string | null; comune_nome: string | null
  provincia_sigla: string | null; citta: string | null
}): string | null {
  if (e.paese && e.paese !== ITALIA) return [e.citta, e.paese_nome ?? e.paese].filter(Boolean).join(', ') || null
  if (e.comune_nome) return `${e.comune_nome}${e.provincia_sigla ? ` (${e.provincia_sigla})` : ''}`
  return null
}

/** Le date dei progetti e dei servizi sono giorni senza ora: si scrivono senza passare dal fuso. */
const dataBreve = (v: string | null | undefined) => (v ? v.slice(0, 10).split('-').reverse().join('/') : '—')
const momento = (v: string | null | undefined) =>
  (v ? new Date(v).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—')
const ore = (v: number | null | undefined) =>
  (v == null ? '—' : Number(v).toLocaleString('it-IT', { maximumFractionDigits: 2 }))

const RUOLI_PROGETTO: Record<string, string> = {
  capofila: 'capofila', partner: 'partner', finanziatore: 'finanziatore',
  ente_attuatore: 'soggetto attuatore', rete: 'rete',
}
const STATI_PROGETTO: Record<string, { etichetta: string; tono: TonoEtichetta }> = {
  da_avviare: { etichetta: 'da avviare', tono: 'informativo' },
  in_corso: { etichetta: 'in corso', tono: 'positivo' },
  concluso: { etichetta: 'concluso', tono: 'neutro' },
}

function messaggioDi(e: unknown, fuoriPerimetro: string): string {
  if (!(e instanceof ErroreApi)) return 'Non è andata a buon fine: controlla la connessione e riprova.'
  return e.stato === 404 ? fuoriPerimetro : e.message
}

/** Aggiunge alle opzioni il valore gia' registrato quando non compare fra quelle attive. */
function conVoce(opzioni: Opzione[], valore: string | null | undefined, etichetta: string | null | undefined) {
  if (!valore || opzioni.some((o) => o.valore === valore)) return opzioni
  return [{ valore, etichetta: etichetta ?? valore }, ...opzioni]
}

function Telefono({ valore }: { valore: string }) {
  const internazionale = telefonoInternazionale(valore) === valore
  return (
    <span className="rete-con-etichetta">
      <a href={`tel:${valore.replace(/[^\d+]/g, '')}`}>{valore}</a>
      {!internazionale && <Etichetta tono="attenzione">non in formato internazionale</Etichetta>}
    </span>
  )
}

/**
 * Scelta del comune per nome. I comuni sono oltre diecimila: si cerca per nome o codice ISTAT e
 * si sceglie fra i risultati, e il comune gia' scelto resta fra i candidati.
 */
export function SceltaComune({ etichetta, valore, cambia, vuota, errore }: {
  etichetta: string; valore: ComuneScelto | null; cambia: (c: ComuneScelto | null) => void; vuota: string
  errore?: string | null
}) {
  const [testo, setTesto] = useState('')
  const ricerca = useTrovati<ComuneScelto[], ComuneScelto>(testo, percorsoComuni, comeArrivano, 300)
  const { trovati } = ricerca
  const candidati = valore && !trovati.some((c) => c.codice_istat === valore.codice_istat) ? [valore, ...trovati] : trovati
  // Provincia davanti al nome (Osservazioni 3, punto 5): nell'elenco si trova prima il territorio.
  const nome = (c: ComuneScelto) =>
    `${c.provincia_sigla ? `${c.provincia_sigla} · ` : ''}${c.nome}${c.soppresso_il ? ' · soppresso' : ''}`
  return (
    <div className="rete-ricerca rete-ricerca-piena">
      <Campo etichetta={`Cerca ${etichetta.toLowerCase()}`} tipo="search" valore={testo} cambia={setTesto}
             segnaposto="Nome del comune, o provincia: NA, Caserta…" />
      <Scelta etichetta={etichetta} vuota={vuota} valore={valore?.codice_istat ?? ''} errore={errore}
              opzioni={candidati.map((c) => ({ valore: c.codice_istat, etichetta: nome(c) }))}
              cambia={(codice) => cambia(candidati.find((c) => c.codice_istat === codice) ?? null)}
              aiuto={aiutoRicerca(ricerca, 'Nessun comune trovato con questo nome.')} />
    </div>
  )
}

const percorsoComuni = (termine: string) => `/riferimenti/comuni?cerca=${encodeURIComponent(termine)}&limite=20`

// --- scheda dell'ente ------------------------------------------------------------------------------

export function Ente() {
  const { id } = useParams()
  const { puo } = useSessione()
  const [ente, setEnte] = useState<DettaglioEnte | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [erroreAzione, setErroreAzione] = useState<string | null>(null)
  const [modifica, setModifica] = useState(false)
  const [disattivazione, setDisattivazione] = useState(false)
  const [referente, setReferente] = useState<{ iniziale?: Referente; rappresentante: boolean } | null>(null)
  const [articolazione, setArticolazione] = useState<{ iniziale?: Articolazione } | null>(null)
  const [mostraDisattivati, setMostraDisattivati] = useState(false)
  const [inCorso, setInCorso] = useState<string | null>(null)
  // «Unisci con un altro ente» (475). Dall'anteprima aperta sull'altro ente («Tieni invece l'altro») si
  // arriva con ?unisci=<ente da unire>: la finestra si apre con quello, e il parametro si toglie. Chi
  // apre un ente gia' unito arriva all'ente tenuto, con un avviso.
  const vaiA = useNavigate()
  const location = useLocation()
  const daEnteUnito = (location.state as { daEnteUnito?: boolean } | null)?.daEnteUnito === true
  const [unione, setUnione] = useState<{ proposta: string | null } | null>(null)
  const [esitoUnione, setEsitoUnione] = useState<string | null>(null)
  useEffect(() => {
    const proposta = new URLSearchParams(location.search).get('unisci')
    if (!proposta) return
    setUnione({ proposta })
    vaiA(location.pathname, { replace: true })
  }, [location.search, location.pathname, vaiA])

  const carica = useCallback(() => {
    setErrore(null)
    api.get<DettaglioEnte>(`/enti/${id}`)
      .then(setEnte)
      .catch((e) => {
        if (e instanceof ErroreApi && e.codice === 'ENTE_UNITO') {
          const { unito_in: tenuto } = e.corpo as { unito_in: string }
          vaiA(`/enti/${tenuto}`, { replace: true, state: { daEnteUnito: true } })
          return
        }
        setErrore(messaggioDi(e, 'Ente non trovato, oppure non rientra nei tuoi incarichi.'))
      })
  }, [id, vaiA])
  useEffect(carica, [carica])

  /** Comando breve che ricarica la scheda: attivazione dell'ente o di un referente. */
  async function esegui(chiave: string, azione: () => Promise<unknown>) {
    setErroreAzione(null)
    setInCorso(chiave)
    try {
      await azione()
      carica()
    } catch (e) {
      setErroreAzione(messaggioDi(e, 'Con i tuoi incarichi non puoi farlo.'))
    } finally {
      setInCorso(null)
    }
  }
  const statoReferente = (r: Referente, attivo: boolean) =>
    esegui(r.id, () => api.patch(`/enti/${id}/referenti/${r.id}`, { attivo }))
  const statoArticolazione = (a: Articolazione, attiva: boolean) =>
    esegui(a.id, () => api.patch(`/enti/${id}/articolazioni/${a.id}`, { attiva }))

  const gestisce = puo('ente.gestisci')
  const e = ente
  const rappresentante = e?.rappresentante_legale ?? null
  const disattivati = (e?.referenti ?? []).filter((r) => !r.attivo).length
  const referenti = (e?.referenti ?? []).filter((r) => mostraDisattivati || r.attivo)
  const progettiAperti = (e?.progetti ?? []).filter((p) => p.stato_date !== 'concluso')
  const progettiConclusi = (e?.progetti ?? []).filter((p) => p.stato_date === 'concluso')

  return (
    <>
      <Intestazione
        titolo={e ? `${e.sigla ? `${e.sigla} — ` : ''}${e.nome}` : 'Ente'}
        sottotitolo={e ? e.categoria_nome ?? 'Profilo non indicato' : undefined}
        azioni={<>
          <Link className="bottone bottone-discreto" to="/enti"><IconaIndietro /> Enti</Link>
          {/* Un ente sostituito da una sede non si modifica e non si riattiva: e' storico. */}
          {e && gestisce && !e.sostituito_da_sede
            && <Bottone variante="secondario" onClick={() => setModifica(true)}>Modifica</Bottone>}
          {e && gestisce && !e.sostituito_da_sede && (
            <Bottone variante="secondario" onClick={() => { setEsitoUnione(null); setUnione({ proposta: null }) }}>
              Unisci con un altro ente
            </Bottone>
          )}
          {e && gestisce && !e.sostituito_da_sede && (e.attivo
            ? <Bottone variante="critico" onClick={() => setDisattivazione(true)}>Disattiva</Bottone>
            : <Bottone variante="secondario" inCorso={inCorso === 'ente'}
                       onClick={() => void esegui('ente', () => api.patch(`/enti/${id}`, { attivo: true }))}>
                Riattiva
              </Bottone>)}
        </>}
      />
      {daEnteUnito && <Avviso>L’ente che hai aperto è stato unito a questo: i suoi dati sono qui.</Avviso>}
      {esitoUnione && <Avviso tipo="successo">{esitoUnione}</Avviso>}

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!errore && !e && <Attesa />}
      {erroreAzione && <Avviso tipo="errore">{erroreAzione}</Avviso>}

      {e && (
        <>
          {/* N-2: l'ente era una sede di CIDIS stessa. Chi arriva qui da un collegamento vecchio
              deve capire subito dove sono finiti i dati, senza cercarli. */}
          {e.sostituito_da_sede ? (
            <Avviso tipo="attenzione">
              <strong>Ora è una sede CIDIS.</strong> Questo ente era una sede di CIDIS registrata
              nell’anagrafica degli enti: è stato disattivato e i riferimenti sono passati alla sede{' '}
              <Link to={`/sedi/${e.sostituito_da_sede}`}>
                {e.sostituito_da_sede_nome ?? 'collegata'}
                {e.sostituito_da_sede_codice ? ` (${e.sostituito_da_sede_codice})` : ''}
              </Link>. I dati qui restano consultabili come storico.
            </Avviso>
          ) : !e.attivo && (
            <Avviso tipo="attenzione">
              <strong>Ente disattivato.</strong> Non compare fra gli enti attivi né nelle ricerche del registro
              dei servizi; i dati restano consultabili.
            </Avviso>
          )}
          {/* Osservazioni 3, punto 8: una sola voce, «Profilo ente» (la colonna resta categoria_ente). */}
          {!e.categoria_ente && (
            <Avviso tipo="attenzione">Profilo dell’ente non indicato: completalo con «Modifica».</Avviso>
          )}

          <div className="rete-dettaglio">
            <Riquadro titolo="Anagrafica">
              <dl className="rete-dati">
                <Dato nome="Acronimo">{e.sigla}</Dato>
                <Dato nome="Nome completo ufficiale">{e.nome}</Dato>
                {/* 475: le altre registrazioni dello stesso ente, unite a questa. */}
                {e.altri_nomi?.length > 0 && (
                  <Dato nome="Anche">
                    {e.altri_nomi.map((a) => `${a.sigla ? `${a.sigla} — ` : ''}${a.nome}`
                      + (a.legacy_id !== null ? ` (vecchio archivio n. ${a.legacy_id})` : '')).join('; ')}
                  </Dato>
                )}
                <Dato nome="Profilo ente">{e.categoria_nome}</Dato>
                <Dato nome="Finanziatore">{FINANZIATORE.find((o) => o.valore === e.finanziatore)?.etichetta}</Dato>
                <Dato nome="Partner formale">{PARTNER_FORMALE.find((o) => o.valore === e.partner_formale)?.etichetta}</Dato>
                {/* Il profilo della classificazione precedente (313) non si chiede piu': resta visibile
                    dove c'e', con un nome che non si confonde con il profilo dell'ente. */}
                {e.profilo_nome && <Dato nome="Classificazione precedente">{e.profilo_nome}</Dato>}
                <Dato nome="Breve descrizione">{e.profilo ? <span className="testo-libero">{e.profilo}</span> : null}</Dato>
                {e.cf_piva && <Dato nome="Codice fiscale o partita IVA">{e.cf_piva}</Dato>}
                {e.natura_nome && <Dato nome="Natura nel vecchio archivio">{e.natura_nome}</Dato>}
              </dl>
            </Riquadro>

            <Riquadro titolo="Sede legale e contatti istituzionali">
              <dl className="rete-dati">
                <Dato nome="Paese">{e.paese_nome}</Dato>
                {e.paese && e.paese !== ITALIA
                  ? <Dato nome="Città">{e.citta}</Dato>
                  : <>
                      <Dato nome="Comune">{luogoEnte(e)}</Dato>
                      <Dato nome="CAP">{e.cap}</Dato>
                    </>}
                <Dato nome="Indirizzo">{e.indirizzo}</Dato>
                <Dato nome="Email istituzionale">{e.email ? <a href={`mailto:${e.email}`}>{e.email}</a> : null}</Dato>
                <Dato nome="Telefono istituzionale">{e.telefono ? <Telefono valore={e.telefono} /> : null}</Dato>
              </dl>
            </Riquadro>
          </div>

          {/* Osservazioni 3, punto 9: un ente chiede servizi da piu' parti di se stesso, e il
              dipartimento che ha chiamato va ritrovato alla richiesta successiva. In alto, sotto
              l'anagrafica, su richiesta della Direzione del 25/10/2026 («Sugli ENTI»). */}
          <Riquadro titolo="Dipartimenti, settori e filiali" denso azioni={gestisce
            ? <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />}
                       onClick={() => setArticolazione({})}>Aggiungi</Bottone>
            : undefined}>
            <Tabella intestazioni={['Nome', 'Nota', 'Servizi registrati',
                                    ...(gestisce ? [<span className="solo-lettore" key="azioni">Azioni</span>] : [])]}
                     vuoto={<Vuoto testo="Nessun dipartimento, settore o filiale registrato." />}>
              {(e.articolazioni ?? []).map((a) => (
                <tr key={a.id}>
                  <td data-etichetta="Nome">
                    <span className="rete-con-etichetta">
                      {a.nome}
                      {!a.attiva && <Etichetta>disattivato</Etichetta>}
                    </span>
                  </td>
                  <td data-etichetta="Nota">{a.nota ?? <span className="tenue">—</span>}</td>
                  <td data-etichetta="Servizi registrati">{a.servizi}</td>
                  {gestisce && (
                    <td data-etichetta="Azioni">
                      <span className="riga riga-a-destra">
                        <Bottone variante="discreto" dimensione="piccolo"
                                 onClick={() => setArticolazione({ iniziale: a })}>Modifica</Bottone>
                        <Bottone variante="discreto" dimensione="piccolo" inCorso={inCorso === a.id}
                                 onClick={() => void statoArticolazione(a, !a.attiva)}>
                          {a.attiva ? 'Disattiva' : 'Riattiva'}
                        </Bottone>
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </Tabella>
          </Riquadro>

          <div className="rete-dettaglio">
            <Riquadro titolo="Sedi CIDIS collegate">
              {e.sedi.length
                ? (
                  <div className="riga-etichette">
                    {e.sedi.map((s) => (
                      <Etichetta key={s.id} tono={s.attiva ? 'accento' : 'neutro'}>
                        {s.nome}{s.attiva ? '' : ' · non attiva'}
                      </Etichetta>
                    ))}
                  </div>
                )
                : <p className="tenue">Nessuna sede CIDIS collegata.</p>}
            </Riquadro>

            <Riquadro titolo="Rappresentante legale" azioni={gestisce && (rappresentante
              ? <>
                  <Bottone variante="secondario" dimensione="piccolo"
                           onClick={() => setReferente({ iniziale: rappresentante, rappresentante: true })}>Modifica</Bottone>
                  <Bottone variante="discreto" dimensione="piccolo" inCorso={inCorso === rappresentante.id}
                           onClick={() => void statoReferente(rappresentante, false)}>Togli</Bottone>
                </>
              : <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />}
                         onClick={() => setReferente({ rappresentante: true })}>Aggiungi</Bottone>)}>
              {rappresentante
                ? (
                  <dl className="rete-dati">
                    <Dato nome="Nome e cognome">{`${rappresentante.nome} ${rappresentante.cognome}`}</Dato>
                    <Dato nome="Email">
                      {rappresentante.email ? <a href={`mailto:${rappresentante.email}`}>{rappresentante.email}</a> : null}
                    </Dato>
                    <Dato nome="Telefono">{rappresentante.telefono ? <Telefono valore={rappresentante.telefono} /> : null}</Dato>
                  </dl>
                )
                : <p className="tenue">Nessun rappresentante legale registrato. Il dato è facoltativo.</p>}
            </Riquadro>
          </div>

          <Riquadro titolo="Referenti" denso azioni={gestisce
            ? <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />}
                       onClick={() => setReferente({ rappresentante: false })}>Aggiungi un referente</Bottone>
            : undefined}>
            {/* La spunta sta nel corpo, allineata alle celle: nell'intestazione, accanto al comando, a
                375 px usciva dal riquadro e faceva scorrere la pagina in orizzontale. */}
            {disattivati > 0 && (
              <div className="rete-nota-riquadro">
                <Spunta etichetta={`Mostra i disattivati (${disattivati})`} valore={mostraDisattivati}
                        cambia={setMostraDisattivati} />
              </div>
            )}
            <Tabella intestazioni={['Nome e cognome', 'Funzione', 'Ufficio / dipartimento', 'Email', 'Telefono',
                                    ...(gestisce ? [<span className="solo-lettore" key="azioni">Azioni</span>] : [])]}
                     vuoto={<Vuoto testo="Nessun referente registrato." />}>
              {referenti.map((r) => (
                <tr key={r.id}>
                  <td data-etichetta="Nome e cognome">
                    <span className="rete-con-etichetta">
                      {r.nome} {r.cognome}
                      {!r.attivo && <Etichetta>disattivato</Etichetta>}
                      {r.rappresentante_legale && <Etichetta tono="informativo">ex rappresentante legale</Etichetta>}
                    </span>
                  </td>
                  <td data-etichetta="Funzione">{r.ruolo ?? <span className="tenue">—</span>}</td>
                  <td data-etichetta="Ufficio / dipartimento">{r.ufficio ?? <span className="tenue">—</span>}</td>
                  <td data-etichetta="Email">{r.email ? <a href={`mailto:${r.email}`}>{r.email}</a> : <span className="tenue">—</span>}</td>
                  <td data-etichetta="Telefono">{r.telefono ? <Telefono valore={r.telefono} /> : <span className="tenue">—</span>}</td>
                  {gestisce && (
                    <td data-etichetta="Azioni">
                      <span className="riga riga-a-destra">
                        <Bottone variante="discreto" dimensione="piccolo"
                                 onClick={() => setReferente({ iniziale: r, rappresentante: r.rappresentante_legale })}>
                          Modifica
                        </Bottone>
                        <Bottone variante="discreto" dimensione="piccolo" inCorso={inCorso === r.id}
                                 onClick={() => void statoReferente(r, !r.attivo)}>
                          {r.attivo ? 'Disattiva' : 'Riattiva'}
                        </Bottone>
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </Tabella>
          </Riquadro>

          {puo('progetto.leggi') && (
            <Riquadro titolo="Progetti collegati" denso>
              {e.progetti.length === 0
                ? <Vuoto testo="Nessun progetto collegato fra quelli che puoi vedere." />
                : <>
                    <TabellaProgetti titolo="In corso o da avviare" righe={progettiAperti} />
                    <TabellaProgetti titolo="Conclusi" righe={progettiConclusi} />
                  </>}
            </Riquadro>
          )}

          {puo('servizio_erogato.leggi') && <ServiziDellEnte enteId={e.id} />}

          <Riquadro titolo="Registrazione">
            <dl className="rete-dati">
              <Dato nome="Inserito il">{momento(e.creato_il)}</Dato>
              {e.aggiornato_il && <Dato nome="Ultima modifica">{momento(e.aggiornato_il)}</Dato>}
              {e.legacy_id !== null && <Dato nome="Ente nel vecchio archivio">n. {e.legacy_id}</Dato>}
            </dl>
          </Riquadro>
        </>
      )}

      {modifica && e && (
        <Modale titolo="Modifica l’ente" larga chiudi={() => setModifica(false)}>
          <ModuloEnte iniziale={e} annulla={() => setModifica(false)}
                      salvato={() => { setModifica(false); carica() }} />
        </Modale>
      )}
      {disattivazione && e && (
        <Modale titolo="Disattiva l’ente" chiudi={() => setDisattivazione(false)} azioni={<>
          <Bottone variante="discreto" onClick={() => setDisattivazione(false)}>Indietro</Bottone>
          <Bottone variante="critico" inCorso={inCorso === 'ente'}
                   onClick={() => void esegui('ente', () => api.patch(`/enti/${id}`, { attivo: false }))
                     .then(() => setDisattivazione(false))}>
            Disattiva l’ente
          </Bottone>
        </>}>
          <p>
            L’ente esce dall’elenco degli enti attivi e dalle ricerche del registro dei servizi. Servizi,
            progetti e referenti collegati restano, e l’ente si può riattivare.
          </p>
        </Modale>
      )}
      {articolazione && e && (
        <Modale titolo={articolazione.iniziale ? 'Modifica il dipartimento' : 'Aggiungi un dipartimento, un settore o una filiale'}
                chiudi={() => setArticolazione(null)}>
          <ModuloArticolazione enteId={e.id} iniziale={articolazione.iniziale}
                               annulla={() => setArticolazione(null)}
                               salvato={() => { setArticolazione(null); carica() }} />
        </Modale>
      )}
      {unione && e && gestisce && (
        <UnisciConEnte ente={e} proposta={unione.proposta} chiudi={() => setUnione(null)}
                       fatto={(testo) => { setUnione(null); setEsitoUnione(testo); carica() }} />
      )}
      {referente && e && (
        <Modale titolo={referente.rappresentante
          ? (referente.iniziale ? 'Modifica il rappresentante legale' : 'Aggiungi il rappresentante legale')
          : (referente.iniziale ? 'Modifica il referente' : 'Aggiungi un referente')}
                chiudi={() => setReferente(null)}>
          <ModuloReferente enteId={e.id} iniziale={referente.iniziale} rappresentante={referente.rappresentante}
                           annulla={() => setReferente(null)}
                           salvato={() => { setReferente(null); carica() }} />
        </Modale>
      )}
    </>
  )
}

function TabellaProgetti({ titolo, righe }: { titolo: string; righe: ProgettoCollegato[] }) {
  return (
    <>
      <h3 className="rete-titoletto-tabella">{titolo}</h3>
      <Tabella intestazioni={['Progetto', 'Ruolo dell’ente', 'Periodo', 'Stato']}
               vuoto={<span className="tenue">Nessun progetto.</span>}>
        {righe.map((p) => (
          <tr key={p.id}>
            <td data-etichetta="Progetto">
              {p.codice_cidis ? `${p.codice_cidis} · ` : ''}
              <Link to={`/progetti/${p.id}`} className="voce-principale">{p.titolo}</Link>
            </td>
            <td data-etichetta="Ruolo dell’ente">{p.ruoli.map((r) => RUOLI_PROGETTO[r] ?? r).join(', ')}</td>
            <td data-etichetta="Periodo" className="mono">{dataBreve(p.data_inizio)} – {dataBreve(p.data_fine)}</td>
            <td data-etichetta="Stato">
              {p.stato_date
                ? <Etichetta tono={STATI_PROGETTO[p.stato_date].tono}>{STATI_PROGETTO[p.stato_date].etichetta}</Etichetta>
                : <span className="tenue">date non indicate</span>}
            </td>
          </tr>
        ))}
      </Tabella>
    </>
  )
}

/** Ultimi servizi resi all'ente, dal registro e nel suo perimetro; l'elenco completo resta nel registro. */
function ServiziDellEnte({ enteId }: { enteId: string }) {
  const aree = useAree()
  const [righe, setRighe] = useState<RigaServizio[] | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  useEffect(() => {
    let valido = true
    api.get<RigaServizio[]>(`/servizi-erogati?ente=${encodeURIComponent(enteId)}&limite=10`)
      .then((r) => { if (valido) setRighe(r) })
      .catch((e) => { if (valido) setErrore(messaggioDi(e, 'Il registro dei servizi non è disponibile con i tuoi incarichi.')) })
    return () => { valido = false }
  }, [enteId])
  const totale = totaleDi(righe)

  return (
    <Riquadro titolo={totale ? `Servizi erogati (${totale})` : 'Servizi erogati'} denso
              azioni={<Link className="bottone bottone-discreto bottone-piccolo" to={`/registro?ente=${enteId}`}>
                Apri nel registro
              </Link>}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!errore && !righe && <Attesa />}
      {righe && (
        <Tabella intestazioni={['Data', 'Tipologia', 'Sede', 'Ore', 'Stato']}
                 vuoto={<Vuoto testo="Nessun servizio erogato a questo ente fra quelli che puoi vedere." />}>
          {righe.map((r) => (
            <tr key={r.id}>
              <td data-etichetta="Data">
                <Link to={`/registro/${r.id}`} className="voce-principale">{dataBreve(r.data)}</Link>
              </td>
              <td data-etichetta="Tipologia">
                <span className="rete-tipologia">
                  <EtichettaArea codice={r.area_codice} colore={aree.coloreDi(r.area_codice)} />
                  {r.tipologia}
                </span>
              </td>
              <td data-etichetta="Sede">{r.sede ?? <span className="tenue">—</span>}</td>
              <td data-etichetta="Ore" className="mono">{ore(r.ore)}</td>
              <td data-etichetta="Stato">
                {r.annullato ? <Etichetta tono="critico">annullato</Etichetta>
                  : r.da_verificare ? <Etichetta tono="attenzione">da verificare</Etichetta>
                    : <Etichetta tono="positivo">registrato</Etichetta>}
              </td>
            </tr>
          ))}
        </Tabella>
      )}
      {righe && totale !== null && totale > righe.length && (
        <p className="nota-elenco">Ultimi {righe.length} servizi su {totale}: l’elenco completo è nel registro.</p>
      )}
    </Riquadro>
  )
}

// --- modulo dell'ente -----------------------------------------------------------------------------

/** «Finanziatore?» e «Partner formale?» (Osservazioni 5 del 28/10/2026, 478): tre risposte ciascuno. */
const FINANZIATORE = [
  { valore: 'si', etichetta: 'Sì' }, { valore: 'no', etichetta: 'No' }, { valore: 'in_parte', etichetta: 'In parte' },
]
const PARTNER_FORMALE = [
  { valore: 'si', etichetta: 'Sì' }, { valore: 'no', etichetta: 'No' },
  { valore: 'in_corso', etichetta: 'In corso di formalizzazione' },
]

type ValoriEnte = {
  sigla: string; nome: string; categoria_ente: string; profilo: string
  finanziatore: string; partner_formale: string
  paese: string; cap: string; citta: string; indirizzo: string; email: string; telefono: string; sedi: string[]
}
type Simile = { id: string; nome: string; sigla: string | null }

function valoriEnte(d?: DettaglioEnte): ValoriEnte {
  return {
    sigla: d?.sigla ?? '', nome: d?.nome ?? '', categoria_ente: d?.categoria_ente ?? '',
    profilo: d?.profilo ?? '', finanziatore: d?.finanziatore ?? '', partner_formale: d?.partner_formale ?? '',
    // Italia predefinita anche sugli enti migrati, che il vecchio archivio non registrava
    // (Osservazioni 3, punto 10): i pochi esteri si correggono a mano, e il campo resta in vista.
    paese: d?.paese ?? ITALIA,
    cap: d?.cap ?? '', citta: d?.citta ?? '', indirizzo: d?.indirizzo ?? '', email: d?.email ?? '',
    telefono: d?.telefono ?? '', sedi: (d?.sedi ?? []).map((s) => s.id).sort(),
  }
}
const comuneDi = (d?: DettaglioEnte): ComuneScelto | null =>
  (d?.comune ? { codice_istat: d.comune, nome: d.comune_nome ?? d.comune, provincia_sigla: d.provincia_sigla } : null)

const CAMPI_LUOGO = ['paese', 'comune', 'cap', 'citta']

/** Corpo della richiesta: per l'Italia comune e CAP, per l'estero la citta'; l'altra coppia parte vuota. */
function corpoEnte(v: ValoriEnte, comune: ComuneScelto | null): Record<string, unknown> {
  const italia = !v.paese || v.paese === ITALIA
  return {
    sigla: v.sigla.trim() || null,
    nome: v.nome.trim(),
    categoria_ente: v.categoria_ente || null,
    finanziatore: v.finanziatore || null,
    partner_formale: v.partner_formale || null,
    profilo: v.profilo.trim() || null,
    paese: v.paese || null,
    comune: italia ? comune?.codice_istat ?? null : null,
    cap: italia ? v.cap.trim() || null : null,
    citta: italia ? null : v.citta.trim() || null,
    indirizzo: v.indirizzo.trim() || null,
    email: v.email.trim() || null,
    telefono: v.telefono.trim() || null,
    sedi: [...v.sedi].sort(),
  }
}

/** Etichette dei campi dell'ente, per gli errori che l'API restituisce con il codice del campo. */
const ETICHETTE_ENTE: Etichette = {
  sigla: 'Acronimo (sigla)', nome: 'Nome completo ufficiale', categoria_ente: 'Profilo ente',
  finanziatore: 'Finanziatore?', partner_formale: 'Partner formale?',
  profilo_ente: 'Profilo (non più richiesto)', profilo: 'Breve descrizione', paese: 'Paese', comune: 'Comune',
  cap: 'CAP', citta: 'Città', indirizzo: 'Indirizzo fisico', email: 'Email istituzionale',
  telefono: 'Telefono istituzionale', sedi: 'Sedi CIDIS collegate', attivo: 'Stato',
  referenti: 'Referenti',
}
const CODICI_CAMPO_ENTE: Record<string, string> = {
  CATEGORIA_ENTE: 'categoria_ente', PROFILO_ENTE: 'profilo_ente', SEDE: 'sedi', TELEFONO_FORMATO: 'telefono',
}

export function ModuloEnte({ iniziale, salvato, annulla }: {
  iniziale?: DettaglioEnte; salvato: (id: string) => void; annulla: () => void
}) {
  const { sedi } = useSessione()
  const { paesi } = usePaesi()
  const categorie = useCatalogo('categoria_ente')
  const [v, setV] = useState<ValoriEnte>(() => valoriEnte(iniziale))
  const [comune, setComune] = useState<ComuneScelto | null>(() => comuneDi(iniziale))
  const [errori, setErrori] = useState<Record<string, string>>({})
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  // N-2: l'API chiede conferma quando il nome contiene «CIDIS». La domanda si pone una volta;
  // rispondendo di si' la richiesta riparte con la conferma.
  const [confermaCidis, setConfermaCidis] = useState(false)
  // Ogni invio non riuscito porta il fuoco sul primo campo segnalato.
  const [tentativo, setTentativo] = useState(0)
  const radice = useRef<HTMLFormElement>(null)
  useFuocoSulPrimoErrore(radice, tentativo)

  const imposta = <K extends keyof ValoriEnte>(chiave: K, valore: ValoriEnte[K]) =>
    setV((attuali) => ({ ...attuali, [chiave]: valore }))
  const italia = !v.paese || v.paese === ITALIA

  // Dipartimenti, settori e filiali subito dopo il profilo (richiesta della Direzione del 25/10/2026,
  // «Sugli ENTI»): alla creazione partono con l'ente, nella modifica si aggiungono dopo il salvataggio.
  // Quelli gia' registrati si modificano e si disattivano dalla scheda dell'ente.
  const esistenti = (iniziale?.articolazioni ?? []).filter((a) => a.attiva).map((a) => a.nome)
  const [nuoveArticolazioni, setNuoveArticolazioni] = useState<string[]>([])
  const [articolazioneScritta, setArticolazioneScritta] = useState('')
  const giaPresente = (nome: string) => [...esistenti, ...nuoveArticolazioni]
    .some((n) => n.trim().toLowerCase() === nome.trim().toLowerCase())
  function aggiungiArticolazione() {
    const nome = articolazioneScritta.trim()
    if (nome && !giaPresente(nome)) setNuoveArticolazioni((a) => [...a, nome])
    setArticolazioneScritta('')
  }
  // Referenti, solo alla creazione: dopo si gestiscono dalla scheda dell'ente, con «Aggiungi un referente».
  const [nuoviReferenti, setNuoviReferenti] = useState<ValoriReferente[]>([])
  const [referenteScritto, setReferenteScritto] = useState<ValoriReferente>(() => valoriReferente())
  const [erroriReferente, setErroriReferente] = useState<Record<string, string>>({})
  const referenteIniziato = Object.values(referenteScritto).some((x) => x.trim())
  function aggiungiReferente() {
    const r = referenteScritto
    const e: Record<string, string> = {}
    if (!r.nome.trim()) e.nome = 'Indicare il nome.'
    if (!r.cognome.trim()) e.cognome = 'Indicare il cognome.'
    if (!emailAccettabile(r.email)) e.email = 'Indirizzo email non valido.'
    if (!telefonoAccettabile(r.telefono)) e.telefono = AIUTO_TELEFONO
    setErroriReferente(e)
    if (Object.keys(e).length) return
    setNuoviReferenti((a) => [...a, r])
    setReferenteScritto(valoriReferente())
  }

  // Possibili doppioni, solo nell'inserimento: enti attivi con nome o acronimo simili.
  const perNome = useTrovati<Simile[], Simile>(iniziale ? '' : v.nome,
    (termine) => `/enti?cerca=${encodeURIComponent(termine)}&limite=5`, comeArrivano, 500)
  const perSigla = useTrovati<Simile[], Simile>(iniziale ? '' : v.sigla,
    (termine) => `/enti?cerca=${encodeURIComponent(termine)}&limite=5`, comeArrivano, 500)
  const simili = [...perNome.trovati, ...perSigla.trovati.filter((s) => !perNome.trovati.some((n) => n.id === s.id))]

  const opzioniSedi = [
    ...vociSedi(sedi),
    // Una sede collegata e poi chiusa non e' fra quelle attive: resta visibile e si puo' togliere.
    ...(iniziale?.sedi ?? []).filter((s) => !sedi.some((a) => a.id === s.id))
      .map((s) => ({ codice: s.id, nome: `${s.nome} · non attiva` })),
  ]

  function convalida(): Record<string, string> {
    const e: Record<string, string> = {}
    if (v.nome.trim().length < 2) e.nome = 'Indicare il nome completo ufficiale.'
    if (!v.categoria_ente) e.categoria_ente = 'Scegliere il profilo dell’ente.'
    if (italia && v.cap.trim() && !/^[0-9]{5}$/.test(v.cap.trim())) e.cap = 'Il CAP è di cinque cifre.'
    // Email e telefono migrati che oggi non si accetterebbero non bloccano la correzione degli altri
    // campi: si controllano solo se cambiano, come fa l'API.
    const emailToccata = !iniziale || v.email.trim() !== (iniziale.email ?? '')
    if (emailToccata && !emailAccettabile(v.email)) e.email = 'Indirizzo email non valido.'
    const telefonoToccato = !iniziale || v.telefono.trim() !== (iniziale.telefono ?? '')
    if (telefonoToccato && !telefonoAccettabile(v.telefono)) e.telefono = AIUTO_TELEFONO
    // Un referente scritto e non aggiunto andrebbe perso senza avviso.
    if (!iniziale && referenteIniziato) e.referenti = 'Premi «Aggiungi il referente» oppure svuota i suoi campi.'
    return e
  }

  async function salva() {
    setErrore(null)
    const trovati = convalida()
    setErrori(trovati)
    if (Object.keys(trovati).length) {
      setErrore('Correggere i campi segnalati.')
      setTentativo((n) => n + 1)
      return
    }

    const completo = corpoEnte(v, comune)
    let corpo = completo
    // Il nome scritto e non ancora aggiunto vale come aggiunto: e' quello che ci si aspetta salvando.
    const articolazioni = articolazioneScritta.trim() && !giaPresente(articolazioneScritta)
      ? [...nuoveArticolazioni, articolazioneScritta.trim()] : nuoveArticolazioni
    if (iniziale) {
      // Sulla modifica partono solo i campi cambiati: l'API controlla quello che si tocca. Il luogo
      // parte intero, perche' passando dall'Italia all'estero comune e CAP vanno svuotati insieme.
      const prima = corpoEnte(valoriEnte(iniziale), comuneDi(iniziale))
      corpo = Object.fromEntries(Object.entries(completo)
        .filter(([chiave, valore]) => JSON.stringify(valore) !== JSON.stringify(prima[chiave])))
      if (CAMPI_LUOGO.some((c) => c in corpo)) for (const c of CAMPI_LUOGO) corpo[c] = completo[c]
      if (!Object.keys(corpo).length && !articolazioni.length) { salvato(iniziale.id); return }
    }
    setInCorso(true)
    try {
      const conConferma = confermaCidis ? { ...corpo, conferma_sede_cidis: true } : corpo
      if (iniziale) {
        if (Object.keys(corpo).length) await api.patch(`/enti/${iniziale.id}`, conConferma)
        for (const nome of articolazioni) {
          // Dopo un errore a meta' si riprova con lo stesso elenco: quelli gia' entrati non si ripetono.
          await api.post(`/enti/${iniziale.id}/articolazioni`, { nome }).catch((e: unknown) => {
            if (!(e instanceof ErroreApi && e.codice === 'ARTICOLAZIONE_PRESENTE')) throw e
          })
        }
        salvato(iniziale.id)
      } else {
        const creato = await api.post<{ id: string }>('/enti', {
          ...conConferma,
          articolazioni: articolazioni.map((nome) => ({ nome })),
          referenti: nuoviReferenti.map(corpoReferente),
        })
        salvato(creato.id)
      }
    } catch (e) {
      if (e instanceof ErroreApi && e.codice === 'POSSIBILE_SEDE_CIDIS') {
        setConfermaCidis(true)
        setErrore(null)
        setErrori({})
        setInCorso(false)
        return
      }
      setErrore(messaggioDi(e, 'Con i tuoi incarichi non puoi farlo.'))
      setErrori(erroriDellApi(e, CODICI_CAMPO_ENTE))
      setTentativo((n) => n + 1)
    } finally {
      setInCorso(false)
    }
  }

  return (
    <form ref={radice} onSubmit={(e) => { e.preventDefault(); void salva() }} noValidate>
      <h3 className="rete-titoletto">Identificazione</h3>
      <div className="griglia-modulo">
        <Campo etichetta="Acronimo (sigla)" maxLength={30} valore={v.sigla} cambia={(x) => imposta('sigla', x)}
               errore={errori.sigla} aiuto="Facoltativo." />
        <Campo etichetta="Nome completo ufficiale" obbligatorio maxLength={300} valore={v.nome}
               cambia={(x) => imposta('nome', x)} errore={errori.nome} />
        {/* Una tendina sola (Osservazioni 3, punto 8): «categoria sintetica» erano appunti interni
            di CIDIS e non deve comparire. Le voci restano quelle di ref.categoria_ente, che e' il
            catalogo della colonna scritta. */}
        <Scelta etichetta="Profilo ente" obbligatorio vuota="— seleziona —" valore={v.categoria_ente}
                cambia={(x) => imposta('categoria_ente', x)} errore={errori.categoria_ente}
                opzioni={conVoce(categorie.voci.map((c) => ({ valore: c.codice, etichetta: c.nome })),
                  iniziale?.categoria_ente, iniziale?.categoria_nome)} />
        <Scelta etichetta="Finanziatore?" vuota="— non indicato —" valore={v.finanziatore}
                cambia={(x) => imposta('finanziatore', x)} errore={errori.finanziatore} opzioni={FINANZIATORE} />
        <Scelta etichetta="Partner formale?" vuota="— non indicato —" valore={v.partner_formale}
                cambia={(x) => imposta('partner_formale', x)} errore={errori.partner_formale} opzioni={PARTNER_FORMALE} />
      </div>
      <fieldset className="gruppo-caselle">
        <legend>Dipartimenti, settori e filiali</legend>
        {[...esistenti, ...nuoveArticolazioni].length > 0 && (
          <div className="riga-etichette">
            {esistenti.map((nome) => <Etichetta key={`g-${nome}`}>{nome}</Etichetta>)}
            {nuoveArticolazioni.map((nome) => (
              <span key={`n-${nome}`} className="rete-con-etichetta">
                <Etichetta tono="accento">{nome}</Etichetta>
                <Bottone variante="discreto" dimensione="piccolo" aria-label={`Togli ${nome}`}
                         onClick={() => setNuoveArticolazioni((a) => a.filter((x) => x !== nome))}>Togli</Bottone>
              </span>
            ))}
          </div>
        )}
        <div className="rete-voce-modulo">
          <Campo etichetta="Aggiungi un dipartimento, un settore o una filiale" maxLength={200}
                 valore={articolazioneScritta} cambia={setArticolazioneScritta}
                 segnaposto="Per esempio: Pronto Soccorso, Caritas, Parrocchia di …"
                 aiuto={iniziale ? 'Quelli già registrati si modificano dalla scheda dell’ente.' : 'Facoltativo: se ne aggiungono quanti servono.'} />
          <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />}
                   disabled={!articolazioneScritta.trim()} onClick={aggiungiArticolazione}>
            Aggiungi
          </Bottone>
        </div>
      </fieldset>
      {simili.length > 0 && (
        <Avviso tipo="informazione">
          <strong>Enti già registrati con nome o acronimo simili</strong>
          <ul className="elenco-avviso">
            {simili.map((s) => <li key={s.id}>{s.sigla ? `${s.sigla} — ` : ''}{s.nome}</li>)}
          </ul>
          Prima di inserire l’ente controllare che non sia già presente.
        </Avviso>
      )}
      <AreaTesto etichetta="Breve descrizione" righe={3} maxLength={1000}
                 valore={v.profilo} cambia={(x) => imposta('profilo', x)} errore={errori.profilo} />

      <h3 className="rete-titoletto">Sede legale e contatti istituzionali</h3>
      <div className="griglia-modulo">
        <Scelta etichetta="Paese" obbligatorio={!iniziale || !!iniziale.paese}
                vuota={iniziale && !iniziale.paese ? '— non indicato —' : undefined}
                valore={v.paese} cambia={(x) => imposta('paese', x)} errore={errori.paese}
                opzioni={conVoce(paesi.map((p) => ({ valore: p.codice_iso3, etichetta: p.nome })),
                  iniziale?.paese, iniziale?.paese_nome)}
                aiuto={italia ? 'Per l’Italia si indicano comune e CAP.' : 'Per un ente estero si indica la città.'} />
        {italia
          ? <>
              <SceltaComune etichetta="Comune" vuota="— nessuno —" valore={comune} cambia={setComune} errore={errori.comune} />
              <Campo etichetta="CAP" maxLength={5} modalitaTastiera="numeric" valore={v.cap}
                     cambia={(x) => imposta('cap', x)} errore={errori.cap} />
            </>
          : <Campo etichetta="Città" maxLength={120} valore={v.citta} cambia={(x) => imposta('citta', x)}
                   errore={errori.citta} />}
        <Campo etichetta="Indirizzo fisico" maxLength={300} valore={v.indirizzo} cambia={(x) => imposta('indirizzo', x)}
               errore={errori.indirizzo} />
        <Campo etichetta="Email istituzionale" tipo="email" maxLength={200} valore={v.email}
               cambia={(x) => imposta('email', x)} errore={errori.email} />
        <Campo etichetta="Telefono istituzionale" tipo="tel" maxLength={40} segnaposto="+39 075 5720895"
               valore={v.telefono} cambia={(x) => imposta('telefono', x)}
               errore={errori.telefono} aiuto={AIUTO_TELEFONO} />
      </div>

      <fieldset className={`gruppo-caselle${errori.sedi ? ' campo-errato' : ''}`}>
        <legend>Sedi CIDIS collegate</legend>
        {opzioniSedi.length
          ? <Caselle voci={opzioniSedi} scelte={v.sedi} cambia={(x) => imposta('sedi', x)} />
          : <p className="tenue">Nessuna sede CIDIS attiva.</p>}
        {errori.sedi && <p className="campo-errore" role="alert">{errori.sedi}</p>}
      </fieldset>

      {!iniziale && (
        <>
          <h3 className="rete-titoletto">Referenti</h3>
          {nuoviReferenti.length > 0 && (
            <ul className="elenco-avviso">
              {nuoviReferenti.map((r, i) => (
                <li key={`${r.nome}-${r.cognome}-${i}`}>
                  {r.nome} {r.cognome}{r.ruolo.trim() ? ` · ${r.ruolo.trim()}` : ''}
                  {r.telefono.trim() ? ` · ${r.telefono.trim()}` : ''}{r.email.trim() ? ` · ${r.email.trim()}` : ''}{' '}
                  <Bottone variante="discreto" dimensione="piccolo" aria-label={`Togli ${r.nome} ${r.cognome}`}
                           onClick={() => setNuoviReferenti((a) => a.filter((_, j) => j !== i))}>Togli</Bottone>
                </li>
              ))}
            </ul>
          )}
          <div className="griglia-modulo">
            <Campo etichetta="Nome" maxLength={100} valore={referenteScritto.nome} errore={erroriReferente.nome}
                   cambia={(x) => setReferenteScritto((a) => ({ ...a, nome: x }))} />
            <Campo etichetta="Cognome" maxLength={100} valore={referenteScritto.cognome} errore={erroriReferente.cognome}
                   cambia={(x) => setReferenteScritto((a) => ({ ...a, cognome: x }))} />
            <Campo etichetta="Funzione" maxLength={120} valore={referenteScritto.ruolo}
                   cambia={(x) => setReferenteScritto((a) => ({ ...a, ruolo: x }))} />
            <Campo etichetta="Telefono" tipo="tel" maxLength={40} segnaposto="+39 333 1234567"
                   valore={referenteScritto.telefono} errore={erroriReferente.telefono}
                   cambia={(x) => setReferenteScritto((a) => ({ ...a, telefono: x }))} />
            <Campo etichetta="Email" tipo="email" maxLength={200} valore={referenteScritto.email}
                   errore={erroriReferente.email} cambia={(x) => setReferenteScritto((a) => ({ ...a, email: x }))} />
          </div>
          <div className="riga">
            <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />}
                     disabled={!referenteIniziato} onClick={aggiungiReferente}>
              Aggiungi il referente
            </Bottone>
          </div>
          {errori.referenti && <p className="campo-errore" role="alert">{errori.referenti}</p>}
        </>
      )}

      {confermaCidis && (
        <Avviso tipo="attenzione">
          <strong>Il nome contiene «CIDIS».</strong> Le sedi di CIDIS si registrano fra le sedi, non
          nell’anagrafica degli enti: è la correzione decisa per i record che erano registrati così.
          Se questo è davvero un ente terzo, salva di nuovo per proseguire.
        </Avviso>
      )}
      <AvvisoModulo errore={errore} errori={errori} etichette={ETICHETTE_ENTE} />
      <div className="azioni-modulo">
        <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>
        <Bottone variante="primario" tipo="submit" inCorso={inCorso}>
          {confermaCidis
            ? 'Sì, è un ente terzo: salva'
            : iniziale ? 'Salva le modifiche' : 'Inserisci l’ente'}
        </Bottone>
      </div>
    </form>
  )
}

// --- modulo del referente ---------------------------------------------------------------------------

type ValoriReferente = { nome: string; cognome: string; ruolo: string; ufficio: string; email: string; telefono: string }

function valoriReferente(r?: Referente): ValoriReferente {
  return {
    nome: r?.nome ?? '', cognome: r?.cognome ?? '', ruolo: r?.ruolo ?? '', ufficio: r?.ufficio ?? '',
    email: r?.email ?? '', telefono: r?.telefono ?? '',
  }
}
const corpoReferente = (v: ValoriReferente): Record<string, unknown> => ({
  nome: v.nome.trim(), cognome: v.cognome.trim(), ruolo: v.ruolo.trim() || null, ufficio: v.ufficio.trim() || null,
  email: v.email.trim() || null, telefono: v.telefono.trim() || null,
})

const ETICHETTE_REFERENTE: Etichette = {
  nome: 'Nome', cognome: 'Cognome', ruolo: 'Funzione', ufficio: 'Ufficio / dipartimento di riferimento',
  email: 'Email', telefono: 'Telefono', rappresentante_legale: 'Rappresentante legale', attivo: 'Stato',
}

/**
 * Dipartimento, filiale o ufficio di un ente (Osservazioni 3, punto 9). Nome in testo libero: i
 * venti dipartimenti di un ospedale non stanno in nessun catalogo. Una volta scritto resta
 * nell'elenco dell'ente e si ritrova registrando il servizio successivo.
 */
export function ModuloArticolazione({ enteId, iniziale, salvato, annulla }: {
  enteId: string; iniziale?: Articolazione; salvato: (id: string) => void; annulla: () => void
}) {
  const [nome, setNome] = useState(iniziale?.nome ?? '')
  const [nota, setNota] = useState(iniziale?.nota ?? '')
  const [errori, setErrori] = useState<Record<string, string>>({})
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [tentativo, setTentativo] = useState(0)
  const radice = useRef<HTMLFormElement>(null)
  useFuocoSulPrimoErrore(radice, tentativo)

  async function salva() {
    setErrore(null)
    if (!nome.trim()) {
      setErrori({ nome: 'Indicare il nome del dipartimento, del settore o della filiale.' })
      setErrore('Correggere i campi segnalati.')
      setTentativo((n) => n + 1)
      return
    }
    setErrori({})
    const corpo = { nome: nome.trim(), nota: nota.trim() || null }
    setInCorso(true)
    try {
      if (iniziale) {
        await api.patch(`/enti/${enteId}/articolazioni/${iniziale.id}`, corpo)
        salvato(iniziale.id)
      } else {
        const r = await api.post<{ id: string }>(`/enti/${enteId}/articolazioni`, corpo)
        salvato(r.id)
      }
    } catch (err) {
      setErrore(messaggioDi(err, 'Con i tuoi incarichi non puoi farlo.'))
      setErrori(erroriDellApi(err, { ARTICOLAZIONE_PRESENTE: 'nome' }))
      setTentativo((n) => n + 1)
    } finally {
      setInCorso(false)
    }
  }

  return (
    <form ref={radice} onSubmit={(ev) => { ev.preventDefault(); void salva() }} noValidate>
      <div className="griglia-modulo">
        <Campo etichetta="Nome" obbligatorio maxLength={200} valore={nome} cambia={setNome} errore={errori.nome}
               aiuto="Per esempio: Pronto Soccorso, Pediatria, Caritas, Filiale di Caserta." />
        <Campo etichetta="Nota" maxLength={500} valore={nota} cambia={setNota} errore={errori.nota}
               aiuto="Facoltativa." />
      </div>
      <AvvisoModulo errore={errore} errori={errori}
                    etichette={{ nome: 'Nome', nota: 'Nota' }} />
      <div className="azioni-modulo">
        <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>
        <Bottone variante="primario" tipo="submit" inCorso={inCorso}>{iniziale ? 'Salva le modifiche' : 'Aggiungi'}</Bottone>
      </div>
    </form>
  )
}

/**
 * Referente o rappresentante legale. Per il rappresentante la STRUTTURA chiede nome, cognome, email
 * e telefono; ruolo e ufficio sono dei referenti.
 */
function ModuloReferente({ enteId, iniziale, rappresentante, salvato, annulla }: {
  enteId: string; iniziale?: Referente; rappresentante: boolean; salvato: () => void; annulla: () => void
}) {
  const [v, setV] = useState<ValoriReferente>(() => valoriReferente(iniziale))
  const [errori, setErrori] = useState<Record<string, string>>({})
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [tentativo, setTentativo] = useState(0)
  const radice = useRef<HTMLFormElement>(null)
  useFuocoSulPrimoErrore(radice, tentativo)
  const imposta = (chiave: keyof ValoriReferente, valore: string) => setV((a) => ({ ...a, [chiave]: valore }))

  async function salva() {
    setErrore(null)
    const e: Record<string, string> = {}
    if (!v.nome.trim()) e.nome = 'Indicare il nome.'
    if (!v.cognome.trim()) e.cognome = 'Indicare il cognome.'
    const emailToccata = !iniziale || v.email.trim() !== (iniziale.email ?? '')
    if (emailToccata && !emailAccettabile(v.email)) e.email = 'Indirizzo email non valido.'
    const telefonoToccato = !iniziale || v.telefono.trim() !== (iniziale.telefono ?? '')
    if (telefonoToccato && !telefonoAccettabile(v.telefono)) e.telefono = AIUTO_TELEFONO
    setErrori(e)
    if (Object.keys(e).length) {
      setErrore('Correggere i campi segnalati.')
      setTentativo((n) => n + 1)
      return
    }

    let corpo = corpoReferente(v)
    if (iniziale) {
      const prima = corpoReferente(valoriReferente(iniziale))
      corpo = Object.fromEntries(Object.entries(corpo).filter(([k, x]) => x !== prima[k]))
      if (!Object.keys(corpo).length) { salvato(); return }
    }
    setInCorso(true)
    try {
      if (iniziale) await api.patch(`/enti/${enteId}/referenti/${iniziale.id}`, corpo)
      else await api.post(`/enti/${enteId}/referenti`, { ...corpo, rappresentante_legale: rappresentante })
      salvato()
    } catch (err) {
      setErrore(messaggioDi(err, 'Con i tuoi incarichi non puoi farlo.'))
      setErrori(erroriDellApi(err, { TELEFONO_FORMATO: 'telefono' }))
      setTentativo((n) => n + 1)
    } finally {
      setInCorso(false)
    }
  }

  return (
    <form ref={radice} onSubmit={(ev) => { ev.preventDefault(); void salva() }} noValidate>
      <div className="griglia-modulo">
        <Campo etichetta="Nome" obbligatorio maxLength={100} valore={v.nome} cambia={(x) => imposta('nome', x)} errore={errori.nome} />
        <Campo etichetta="Cognome" obbligatorio maxLength={100} valore={v.cognome} cambia={(x) => imposta('cognome', x)} errore={errori.cognome} />
        {!rappresentante && (
          <>
            <Campo etichetta="Funzione" maxLength={120} valore={v.ruolo} cambia={(x) => imposta('ruolo', x)}
                   errore={errori.ruolo} />
            <Campo etichetta="Ufficio / dipartimento di riferimento" maxLength={200} valore={v.ufficio}
                   cambia={(x) => imposta('ufficio', x)} errore={errori.ufficio} />
          </>
        )}
        <Campo etichetta="Email" tipo="email" maxLength={200} valore={v.email} cambia={(x) => imposta('email', x)} errore={errori.email} />
        <Campo etichetta="Telefono" tipo="tel" maxLength={40} segnaposto="+39 333 1234567" valore={v.telefono}
               cambia={(x) => imposta('telefono', x)} errore={errori.telefono} aiuto={AIUTO_TELEFONO} />
      </div>
      <AvvisoModulo errore={errore} errori={errori} etichette={ETICHETTE_REFERENTE} />
      <div className="azioni-modulo">
        <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>
        <Bottone variante="primario" tipo="submit" inCorso={inCorso}>{iniziale ? 'Salva le modifiche' : 'Aggiungi'}</Bottone>
      </div>
    </form>
  )
}
